import React from "react";
import { Link } from "react-router-dom";
import { PlaySquare, Lock, RotateCcw, Crown } from "lucide-react";
import { MainLayout } from "@/layouts/MainLayout";
import { useMockQuery } from "@/hooks/useMockQuery";
import { apiClient } from "@/lib/apiClient";
import { ReplayCard } from "@/components/cards/ReplayCard";
import { Countdown } from "@/components/Countdown";
import { EventStatusBadge } from "@/components/EventStatusBadge";
import { ErrorState } from "@/components/states/ErrorState";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

function PremiumReplayCard({ item }) {
  const { event, available, locked, expired, expiresAt } = item;
  return (
    <div
      data-testid={`premium-replay-${event.id}`}
      className="group overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-transform duration-300 hover:-translate-y-1 hover:shadow-md"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={event.cover}
          alt={event.title}
          loading="lazy"
          className={`h-full w-full object-cover transition-transform duration-300 group-hover:scale-105 ${
            locked ? "blur-[2px] grayscale" : ""
          }`}
        />
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute left-3 top-3 flex gap-2">
          <EventStatusBadge status="premium" size="sm" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-brand shadow-lg">
            {locked ? <Lock className="h-6 w-6" /> : <RotateCcw className="h-6 w-6" />}
          </span>
        </div>
        <span className="absolute bottom-3 left-3 rounded-full bg-black/65 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white backdrop-blur-md">
          Replay · {event.replay.durationHours}h window
        </span>
      </div>
      <div className="space-y-3 p-5">
        <h3 className="line-clamp-1 font-heading text-base font-bold tracking-tight text-foreground">
          {event.title}
        </h3>
        {available ? (
          <>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              Expires in{" "}
              <Countdown target={expiresAt} variant="inline" className="font-semibold text-foreground" />
            </p>
            <Button asChild className="w-full rounded-full bg-brand hover:bg-brand-hover">
              <Link to={`/live-access/${event.id}`} data-testid={`replay-watch-${event.id}`}>
                <RotateCcw className="mr-1.5 h-4 w-4" /> Watch replay
              </Link>
            </Button>
          </>
        ) : expired ? (
          <>
            <p className="text-xs text-muted-foreground">Your replay window has expired.</p>
            <Button disabled className="w-full rounded-full" variant="outline">
              Replay expired
            </Button>
          </>
        ) : (
          <>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock className="h-3.5 w-3.5" /> Unlock with a ticket to watch the replay
            </p>
            <Button asChild variant="outline" className="w-full rounded-full">
              <Link to={`/event/${event.id}`} data-testid={`replay-unlock-${event.id}`}>
                Unlock access
              </Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, count, children }) {
  return (
    <div>
      <div className="mb-5 flex items-center gap-2">
        <Icon className="h-5 w-5 text-brand" />
        <h2 className="font-heading text-xl font-extrabold tracking-tight text-foreground sm:text-2xl">
          {title}
        </h2>
        {typeof count === "number" && (
          <span className="rounded-full bg-secondary px-2.5 py-0.5 text-sm font-semibold text-muted-foreground">
            {count}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

export default function ReplayHub() {
  const { data, loading, error, refetch } = useMockQuery(() => apiClient.getReplayHub(), []);

  const yourReplays = (data?.premium || []).filter((p) => p.available || p.expired);
  const lockedReplays = (data?.premium || []).filter((p) => p.locked);

  return (
    <MainLayout>
      <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            Replays
          </h1>
          <p className="mt-2 text-muted-foreground">
            Catch up on free streams, plus premium replays you've unlocked.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="aspect-video w-full rounded-2xl" />
            ))}
          </div>
        ) : error ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : (
          <div className="space-y-14">
            {yourReplays.length > 0 && (
              <Section title="Your premium replays" icon={Crown} count={yourReplays.length}>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {yourReplays.map((p) => (
                    <PremiumReplayCard key={p.event.id} item={p} />
                  ))}
                </div>
              </Section>
            )}

            {lockedReplays.length > 0 && (
              <Section title="Premium replays" icon={Lock} count={lockedReplays.length}>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {lockedReplays.map((p) => (
                    <PremiumReplayCard key={p.event.id} item={p} />
                  ))}
                </div>
              </Section>
            )}

            <Section title="Free replays" icon={PlaySquare} count={data.free.length}>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {data.free.map((r) => (
                  <ReplayCard key={r.id} replay={r} />
                ))}
              </div>
            </Section>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
